import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  alpha,
  IconButton,
  Tooltip,
  Collapse,
  useTheme,
} from "@mui/material";
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { documentService } from "../../api/services/documentService";
import { QK } from "../../base/qk";
import CreateCategoryDialog from "./CreateCategoryDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

const CategoryList = ({ selectedCategory, onSelectCategory }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [openDialog, setOpenDialog] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const { data: categoriesData, isLoading } = useQuery(
    [QK.documentCategories],
    () => documentService.getCategories(),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  const categories = Array.isArray(categoriesData) ? categoriesData : categoriesData?.results || [];

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      enqueueSnackbar("Category name is required", { variant: "error" });
      return;
    }

    try {
      if (editingCategory) {
        await documentService.updateCategory(editingCategory.id, { name: categoryName });
        enqueueSnackbar("Category updated successfully", { variant: "success" });
      } else {
        await documentService.createCategory({ name: categoryName });
        enqueueSnackbar("Category created successfully", { variant: "success" });
      }
      queryClient.invalidateQueries([QK.documentCategories]);
      setOpenDialog(false);
      setCategoryName("");
      setEditingCategory(null);
    } catch (error) {
      enqueueSnackbar(editingCategory ? "Failed to update category" : "Failed to create category", {
        variant: "error"
      });
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      await documentService.deleteCategory(categoryToDelete.id);
      queryClient.invalidateQueries([QK.documentCategories]);
      enqueueSnackbar("Category deleted successfully", { variant: "success" });
      if (selectedCategory === categoryToDelete.id) onSelectCategory(null);
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    } catch (error) {
      enqueueSnackbar("Failed to delete category", { variant: "error" });
    }
  };

  const confirmDelete = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const toggleExpand = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const renderCategoryTree = (items, level = 0) => {
    if (!items || items.length === 0) return null;

    return items.map((category) => {
      const hasSubcategories = category.subcategories && category.subcategories.length > 0;
      const isExpanded = expandedCategories[category.id] || false;

      return (
        <Box key={category.id} sx={{ ml: level > 0 ? 2 : 0 }}>
          <Box
            sx={{
              px: 2,
              py: 1,
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: selectedCategory === category.id ? alpha(theme.palette.primary.main, 0.08) : "transparent",
              color: selectedCategory === category.id ? theme.palette.primary.main : "#475569",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            <Box
              onClick={() => onSelectCategory(category.id)}
              sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}
            >
              {hasSubcategories ? (
                isExpanded ? (
                  <FolderOpenIcon sx={{ fontSize: 20 }} />
                ) : (
                  <FolderIcon sx={{ fontSize: 20 }} />
                )
              ) : (
                <FolderIcon sx={{ fontSize: 20 }} />
              )}
              <Typography variant="body2" fontWeight={500}>
                {category.name}
              </Typography>
              <Chip
                label={category.document_count || 0}
                size="small"
                sx={{
                  bgcolor: "transparent",
                  color: "#94A3B8",
                  fontWeight: 500,
                  fontSize: "11px",
                }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {hasSubcategories && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(category.id);
                  }}
                  sx={{ color: "#94A3B8", p: 0.5 }}
                >
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              )}
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingCategory(category);
                    setCategoryName(category.name);
                    setOpenDialog(true);
                  }}
                  sx={{ color: "#94A3B8", p: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDelete(category);
                  }}
                  sx={{ color: "#94A3B8", p: 0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {hasSubcategories && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 1 }}>
                {renderCategoryTree(category.subcategories, level + 1)}
              </Box>
            </Collapse>
          )}
        </Box>
      );
    });
  };

  const rootCategories = categories.filter((cat) => !cat.parent);

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: "16px",
        border: "1px solid #E2E8F0",
        position: "sticky",
        top: 24,
        bgcolor: "#FFFFFF",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} color="#0F172A" fontSize="0.875rem">
          Categories
        </Typography>
        <Tooltip title="Add Category">
          <IconButton
            size="small"
            onClick={() => {
              setEditingCategory(null);
              setCategoryName("");
              setOpenDialog(true);
            }}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.16) },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box
          onClick={() => onSelectCategory(null)}
          sx={{
            px: 2,
            py: 1,
            borderRadius: "8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: !selectedCategory ? alpha(theme.palette.primary.main, 0.08) : "transparent",
            color: !selectedCategory ? theme.palette.primary.main : "#475569",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          <FolderIcon sx={{ fontSize: 20 }} />
          <Typography variant="body2" fontWeight={500}>
            All Documents
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          renderCategoryTree(rootCategories)
        )}
      </Box>

      <CreateCategoryDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setEditingCategory(null);
          setCategoryName("");
        }}
        onSave={handleSaveCategory}
        categoryName={categoryName}
        setCategoryName={setCategoryName}
        editingCategory={editingCategory}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleDeleteCategory}
        itemName={categoryToDelete?.name}
        title="Delete Category"
        message={`Are you sure you want to delete the category "${categoryToDelete?.name}"?`}
        confirmText="Delete Category"
      />
    </Paper>
  );
};

export default CategoryList;